// src/components/Renewals.js
import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Modal,
  Box,
  TextField,
  Autocomplete,
  IconButton,
  Container,
  Chip,
  Card,
  CardContent,
  Fade,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  InputAdornment,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  Add,
  Delete,
  Logout,
  Dashboard,
  Autorenew,
  Close,
  CheckCircle,
  Error as ErrorIcon,
  ArrowBack,
  Search,
  VpnKey,
  Extension,
  CalendarToday,
  Person,
  Warning,
  TrendingUp,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { ref, set, get, child, update, remove } from 'firebase/database';
import { auth, database } from '../firebase/Config';
import { useNavigate } from 'react-router-dom';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  borderRadius: '16px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  p: 4,
};

const Renewals = () => {
  const [licenses, setLicenses] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [filteredRenewals, setFilteredRenewals] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [pluginType, setPluginType] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [renewalData, setRenewalData] = useState({
    renewalDate: new Date().toLocaleDateString(),
    expirationDate: '',
    renewedBy: '',
  });
  const [userEmail, setUserEmail] = useState(null);
  const [licenseStats, setLicenseStats] = useState({
    trials: 0,
    fullLicenses: 0,
  });
  const navigate = useNavigate();

  // Estados para validação e feedback
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    renewalId: null,
    licenseKey: '',
    renewalDate: '',
  });

  // Funções de feedback
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Validação de formulário
  const validateForm = () => {
    const newErrors = {};

    if (!selectedLicense) {
      newErrors.license = 'Licença é obrigatória';
    }

    if (!renewalData.expirationDate) {
      newErrors.expirationDate = 'Data de expiração é obrigatória';
    } else {
      const expirationDate = new Date(renewalData.expirationDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (expirationDate <= today) {
        newErrors.expirationDate = 'Data de expiração deve ser futura';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpen = () => {
    setErrors({});
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedLicense(null);
    setPluginType('');
    setErrors({});
    setRenewalData({
      renewalDate: new Date().toLocaleDateString(),
      expirationDate: '',
      renewedBy: '',
    });
  };

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setUserEmail(user.email);
    }
  }, []);

  const fetchLicenses = async () => {
    const dbRef = ref(database);
    try {
      const snapshot = await get(child(dbRef, 'licenses'));
      if (snapshot.exists()) {
        const licensesData = snapshot.val();
        const licensesArray = Object.keys(licensesData).map((key) => ({
          id: key,
          ...licensesData[key],
        }));
        setLicenses(licensesArray);
      }
    } catch (error) {
      console.error('Erro ao buscar licenças: ', error);
    }
  };

  const fetchRenewals = async () => {
    setLoading(true);
    const dbRef = ref(database);
    try {
      const snapshot = await get(child(dbRef, 'renewals'));
      if (snapshot.exists()) {
        const renewalsData = snapshot.val();
        const renewalsArray = Object.keys(renewalsData).flatMap((licenseId) =>
          Object.keys(renewalsData[licenseId]).map((renewalId) => ({
            id: renewalId,
            licenseKey: licenseId,
            ...renewalsData[licenseId][renewalId],
          }))
        );
        setRenewals(renewalsArray);
        setFilteredRenewals(renewalsArray);
      } else {
        setRenewals([]);
        setFilteredRenewals([]);
      }
    } catch (error) {
      console.error('Erro ao buscar renovações: ', error);
      showSnackbar('Erro ao carregar renovações', 'error');
    }
    setLoading(false);
  };

  // Função para calcular estatísticas das licenças
  const calculateLicenseStats = () => {
    // Criar um mapa para armazenar apenas a renovação mais recente de cada licença
    const latestRenewalsMap = new Map();

    renewals.forEach((renewal) => {
      const licenseKey = renewal.licenseKey;
      const existingRenewal = latestRenewalsMap.get(licenseKey);

      if (!existingRenewal) {
        latestRenewalsMap.set(licenseKey, renewal);
      } else {
        // Comparar datas de expiração para manter a mais recente
        const currentDate = new Date(renewal.expirationDate);
        const existingDate = new Date(existingRenewal.expirationDate);

        if (currentDate > existingDate) {
          latestRenewalsMap.set(licenseKey, renewal);
        }
      }
    });

    // Calcular estatísticas baseadas nas renovações únicas
    let trials = 0;
    let fullLicenses = 0;

    latestRenewalsMap.forEach((renewal) => {
      if (renewal.renewalDate && renewal.expirationDate) {
        // Converter as datas
        let renewalDate;
        // Verificar se renewalDate está no formato DD/MM/YYYY
        if (renewal.renewalDate.includes('/')) {
          const [day, month, year] = renewal.renewalDate.split('/');
          renewalDate = new Date(`${year}-${month}-${day}T00:00:00`);
        } else {
          renewalDate = new Date(renewal.renewalDate + 'T00:00:00');
        }

        const expirationDate = new Date(renewal.expirationDate + 'T00:00:00');

        // Calcular diferença em dias entre renovação e expiração
        const diffTime = expirationDate - renewalDate;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        // Se a validade é menor que 32 dias, é TRIAL, senão é LICENÇA
        if (diffDays < 32) {
          trials++;
        } else {
          fullLicenses++;
        }
      }
    });

    setLicenseStats({
      trials,
      fullLicenses,
    });
  };

  // Recalcular estatísticas quando renovações mudarem
  useEffect(() => {
    if (renewals.length > 0) {
      calculateLicenseStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renewals]);

  const handleAddRenewal = async () => {
    if (!validateForm()) {
      showSnackbar('Por favor, corrija os erros no formulário', 'error');
      return;
    }

    setSubmitLoading(true);
    try {
      const renewalId = `renewal_${new Date().getTime()}`;

      await set(ref(database, `renewals/${selectedLicense.id}/${renewalId}`), {
        renewalDate: renewalData.renewalDate,
        expirationDate: renewalData.expirationDate,
        renewedBy: userEmail,
        pluginType: selectedLicense.pluginName,
      });

      await update(ref(database, `licenses/${selectedLicense.id}`), {
        lastRenewalDate: renewalData.renewalDate,
        expirationDate: renewalData.expirationDate,
        renewedBy: userEmail,
      });

      showSnackbar('Renovação cadastrada com sucesso!', 'success');
      handleClose();
      fetchRenewals();
    } catch (error) {
      console.error('Erro ao adicionar renovação: ', error);
      showSnackbar('Erro ao cadastrar renovação', 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleOpenDeleteDialog = (renewal) => {
    setDeleteDialog({
      open: true,
      renewalId: renewal.id,
      licenseKey: renewal.licenseKey,
      renewalDate: renewal.renewalDate,
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, renewalId: null, licenseKey: '', renewalDate: '' });
  };

  const handleDeleteRenewal = async () => {
    try {
      await remove(ref(database, `renewals/${deleteDialog.licenseKey}/${deleteDialog.renewalId}`));
      showSnackbar('Renovação excluída com sucesso!', 'success');
      fetchRenewals();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Erro ao excluir renovação: ', error);
      showSnackbar('Erro ao excluir renovação', 'error');
    }
  };

  // Função de pesquisa
  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);

    if (value === '') {
      setFilteredRenewals(renewals);
    } else {
      const filtered = renewals.filter((renewal) => {
        const licenseKey = (renewal.licenseKey || '').toLowerCase();
        const pluginType = (renewal.pluginType || '').toLowerCase();
        const renewedBy = (renewal.renewedBy || '').toLowerCase();

        return (
          licenseKey.includes(value) ||
          pluginType.includes(value) ||
          renewedBy.includes(value)
        );
      });
      setFilteredRenewals(filtered);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilteredRenewals(renewals);
  };

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/login');
  };

  useEffect(() => {
    fetchLicenses();
    fetchRenewals();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Colunas da tabela de renovações
  const columns = [
    {
      field: 'licenseKey',
      headerName: 'Licença',
      width: 220,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%' }}>
          <VpnKey sx={{ color: '#48bb78', fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'pluginType',
      headerName: 'Tipo do Plugin',
      width: 180,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Chip
          icon={<Extension />}
          label={params.value}
          size="small"
          sx={{
            bgcolor: '#f6ad5520',
            color: '#f6ad55',
            fontWeight: 500,
          }}
        />
      ),
    },
    {
      field: 'renewalDate',
      headerName: 'Data de Renovação',
      width: 180,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'expirationDate',
      headerName: 'Data de Expiração',
      width: 180,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        if (!params.value) return '-';
        // Formata a data de YYYY-MM-DD para DD/MM/YYYY
        const date = new Date(params.value + 'T00:00:00');
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      },
    },
    {
      field: 'renewedBy',
      headerName: 'Renovado por',
      width: 200,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Chip
          avatar={<Avatar sx={{ bgcolor: '#f59e0b' }}>{params.value?.charAt(0).toUpperCase()}</Avatar>}
          label={params.value}
          size="small"
          sx={{ maxWidth: '100%' }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Tooltip title="Excluir renovação">
            <IconButton
              onClick={() => handleOpenDeleteDialog(params.row)}
              sx={{
                color: '#e53935',
                '&:hover': { bgcolor: '#e5393520' }
              }}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      {/* Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <IconButton
            onClick={() => navigate('/home')}
            sx={{
              color: '#fff',
              mr: 2,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Dashboard sx={{ mr: 2, fontSize: 32 }} />
          <Typography
            variant="h5"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}
          >
            Renovação de Licenças
          </Typography>
          {userEmail && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                avatar={<Avatar sx={{ bgcolor: '#fff', color: '#f59e0b' }}>{userEmail.charAt(0).toUpperCase()}</Avatar>}
                label={userEmail}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  fontWeight: 500,
                  px: 1,
                }}
              />
              <IconButton
                onClick={handleLogout}
                sx={{
                  color: '#fff',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Logout />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: 4, pb: 4 }}>
        <Fade in timeout={600}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: '#2d3748',
                      mb: 1,
                    }}
                  >
                    <Autorenew sx={{ mr: 1, verticalAlign: 'middle', fontSize: 32, color: '#f59e0b' }} />
                    Histórico de Renovações
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#718096' }}>
                    Total de renovações: <strong>{renewals.length}</strong>
                    {searchTerm && ` | Filtradas: ${filteredRenewals.length}`}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Add />}
                  onClick={handleOpen}
                  sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: '#fff',
                    fontWeight: 600,
                    py: 1.5,
                    px: 3,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s',
                  }}
                >
                  Nova Renovação
                </Button>
              </Box>

              {/* Gráfico de Status de Licenças */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#2d3748',
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <TrendingUp sx={{ color: '#f59e0b' }} />
                  Estatísticas de Licenças (por licença única)
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {/* Card: Licenças TRIAL (< 32 dias) */}
                  <Card
                    elevation={0}
                    sx={{
                      flex: 1,
                      minWidth: 250,
                      background: 'linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)',
                      color: '#fff',
                      borderRadius: 3,
                      overflow: 'hidden',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '150px',
                        height: '150px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '50%',
                        transform: 'translate(50%, -50%)',
                      },
                    }}
                  >
                    <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Warning sx={{ fontSize: 32 }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                            Licenças TRIAL
                          </Typography>
                          <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1 }}>
                            {licenseStats.trials}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        Validade menor que 32 dias
                      </Typography>
                    </CardContent>
                  </Card>

                  {/* Card: Licenças de CLIENTE (>= 32 dias) */}
                  <Card
                    elevation={0}
                    sx={{
                      flex: 1,
                      minWidth: 250,
                      background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                      color: '#fff',
                      borderRadius: 3,
                      overflow: 'hidden',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '150px',
                        height: '150px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '50%',
                        transform: 'translate(50%, -50%)',
                      },
                    }}
                  >
                    <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CheckCircle sx={{ fontSize: 32 }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                            Licenças de CLIENTE
                          </Typography>
                          <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1 }}>
                            {licenseStats.fullLicenses}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        Validade de 32 dias ou mais
                      </Typography>
                    </CardContent>
                  </Card>

                  {/* Card: Total de Licenças Únicas */}
                  <Card
                    elevation={0}
                    sx={{
                      flex: 1,
                      minWidth: 250,
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      color: '#fff',
                      borderRadius: 3,
                      overflow: 'hidden',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '150px',
                        height: '150px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '50%',
                        transform: 'translate(50%, -50%)',
                      },
                    }}
                  >
                    <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <VpnKey sx={{ fontSize: 32 }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                            Total de Licenças Únicas
                          </Typography>
                          <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1 }}>
                            {licenseStats.trials + licenseStats.fullLicenses}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        Licenças ativas no sistema
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              </Box>

              {/* Barra de Pesquisa */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  placeholder="Pesquisar por licença, plugin ou renovado por..."
                  value={searchTerm}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: '#718096' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClearSearch}
                          size="small"
                          sx={{ color: '#718096' }}
                        >
                          <Close />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#f7fafc',
                      '&:hover': {
                        bgcolor: '#edf2f7',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#fff',
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ height: 500, width: '100%' }}>
                <DataGrid
                  rows={filteredRenewals}
                  columns={columns}
                  pageSize={10}
                  rowsPerPageOptions={[5, 10, 20, 50]}
                  checkboxSelection
                  disableSelectionOnClick
                  loading={loading}
                  getRowId={(row) => row.id}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-row:hover': {
                      bgcolor: '#f7fafc',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      bgcolor: '#f7fafc',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Fade>

        {/* Modal de Nova Renovação */}
        <Modal open={open} onClose={handleClose}>
          <Fade in={open}>
            <Box sx={modalStyle}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#2d3748',
                  }}
                >
                  Adicionar Renovação
                </Typography>
                <IconButton onClick={handleClose} sx={{ color: '#718096' }}>
                  <Close />
                </IconButton>
              </Box>

              <Autocomplete
                options={licenses}
                getOptionLabel={(option) => `${option.key} - ${option.userEmail}`}
                value={selectedLicense}
                onChange={(event, newValue) => {
                  setSelectedLicense(newValue);
                  setPluginType(newValue?.pluginName || '');
                  if (errors.license) setErrors({ ...errors, license: null });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Selecione a Licença"
                    fullWidth
                    margin="normal"
                    required
                    error={!!errors.license}
                    helperText={errors.license}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <VpnKey sx={{ color: '#48bb78' }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <TextField
                label="Tipo do Plugin"
                fullWidth
                margin="normal"
                value={pluginType}
                disabled
                helperText="Preenchido automaticamente ao selecionar a licença"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Extension sx={{ color: '#718096' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Data de Expiração"
                fullWidth
                margin="normal"
                type="date"
                required
                value={renewalData.expirationDate}
                onChange={(e) => {
                  setRenewalData({ ...renewalData, expirationDate: e.target.value });
                  if (errors.expirationDate) setErrors({ ...errors, expirationDate: null });
                }}
                error={!!errors.expirationDate}
                helperText={errors.expirationDate || 'Selecione a data de expiração'}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday sx={{ color: '#718096' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Data da Última Renovação"
                fullWidth
                margin="normal"
                value={renewalData.renewalDate}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday sx={{ color: '#718096' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Renovado por"
                fullWidth
                margin="normal"
                value={userEmail}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: '#718096' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleClose}
                  sx={{
                    py: 1.5,
                    borderColor: '#718096',
                    color: '#718096',
                    '&:hover': {
                      borderColor: '#2d3748',
                      bgcolor: '#f7fafc',
                    },
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleAddRenewal}
                  disabled={submitLoading}
                  sx={{
                    py: 1.5,
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: '#fff',
                    fontWeight: 600,
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(245, 158, 11, 0.4)',
                    },
                    '&:disabled': {
                      background: '#cbd5e0',
                    },
                  }}
                >
                  {submitLoading ? (
                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                  ) : (
                    <>
                      <CheckCircle sx={{ mr: 1 }} />
                      Confirmar Renovação
                    </>
                  )}
                </Button>
              </Box>
            </Box>
          </Fade>
        </Modal>

        {/* Dialog de Confirmação de Exclusão */}
        <Dialog
          open={deleteDialog.open}
          onClose={handleCloseDeleteDialog}
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon sx={{ color: '#e53935' }} />
            Confirmar Exclusão
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja excluir a renovação da licença <strong>{deleteDialog.licenseKey}</strong>?
              <br /><br />
              Data da renovação: <strong>{deleteDialog.renewalDate}</strong>
              <br /><br />
              Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleCloseDeleteDialog}
              sx={{ color: '#718096' }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteRenewal}
              variant="contained"
              sx={{
                bgcolor: '#e53935',
                '&:hover': { bgcolor: '#c62828' }
              }}
              startIcon={<Delete />}
            >
              Excluir
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para Feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Renewals;
